import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { first, isEmpty } from 'lodash-es';
import unset from 'lodash-es/unset';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Organization } from 'organization/organization';
import { UserModulePushStatus } from 'user/model/module/push/user-module-push-status';
import { UserAvailabilityType } from 'user/model/user-availability.type';
import { UserMoodFrequency } from 'user/model/user-mood-frequency';
import { UserRole } from 'user/model/user-role';
import { UserStatus } from 'user/model/user-status';
import { UserPartner } from 'user/partner/user-partner';
import { UserRepositoryPort } from 'user/port/user-repository.port';
import { User } from 'user/user';
import { UserAvailability } from 'user/user-availability';
import { UserMetadata } from 'user/user-metadata';
import { UserModulePush } from 'user/user-module-push';
import { UserReference } from 'user/user-reference';

import { AppLocator } from '../../app.locator';

export interface UserModulePushDocument {
	readonly status: string;
	readonly contactEmailAddress: string;
	readonly pushOrgModuleId: string;
	readonly contactLanguage: string;
	readonly contactName: string;
	readonly contactId: string;
	readonly emailId: string;
}

export interface UserAvailabilityDocument {
	readonly dayIndex: number;
	readonly endOffset: number;
	readonly startOffset: number;
	readonly type: string;
	readonly id: string;
}

export interface UserMetadataDocument {
	readonly moodDescriptionShown?: boolean;
	readonly registrationTimestamp: number;
	readonly moodFrequency: string;
	readonly livingAddress: string;
	readonly originAddress: string;
	readonly linkedInUrl: string;
	readonly language: string;
	readonly birthDate: number;
	readonly isGhost: boolean;
	readonly cvUrl: string;
	readonly quote: string;
}

export interface UserReferenceDocument {
	readonly id: string;
	readonly name: string;
	readonly endDate: number;
	readonly startDate: number;
	readonly institution: string;
}

export interface UserDocument {
	readonly id: string;
	readonly role: string;
	readonly hasMfa: boolean;
	readonly coachId: string;
	readonly timeZone: string;
	readonly lastName: string;
	readonly firstName: string;
	readonly status: string;
	readonly pictureUrl: string;
	readonly intercomHash: string;
	readonly emailAddress: string;
	readonly phoneNumber: string;
	readonly partnersId: string[];
	readonly observersId: string[];
	readonly organizationId: string;
	readonly companySectors: string;
	readonly updateTimestamp: number;
	readonly creationTimestamp: number;
	readonly partners: UserPartner[];
	readonly metadata: UserMetadataDocument;
	readonly diplomas: ReadonlyArray<UserReferenceDocument>;
	readonly certificates: ReadonlyArray<UserReferenceDocument>;
	readonly availabilities: ReadonlyArray<UserAvailabilityDocument>;
	readonly modulePushes: ReadonlyArray<UserModulePushDocument>;
}

export type UserState = EntityState<UserDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'users' })
export class UserStore extends EntityStore<UserState> {
	constructor() {
		super();
	}

	public get _userRepositoryPort(): UserRepositoryPort {
		return AppLocator.inject<UserRepositoryPort>(UserRepositoryPort);
	}

	public asDocument(user: User): UserDocument {
		const document = user
			? ({
					modulePushes: (user.modulePushes || []).map((modulePush) =>
						this._asUserModulePushDocument(modulePush)
					),
					availabilities: (user.availabilities || []).map((availability) =>
						this._asUserAvailabilityDocument(availability)
					),
					certificates: (user.certificates || []).map((certificate) =>
						this._asUserReferenceDocument(certificate)
					),
					diplomas: (user.diplomas || []).map((diploma) =>
						this._asUserReferenceDocument(diploma)
					),
					partners: user.partners,
					metadata: {
						quote: user.metadata?.quote,
						cvUrl: user.metadata?.cvUrl,
						isGhost: user.isGhost,
						language: user.metadata?.language,
						linkedInUrl: user.metadata?.linkedInUrl,
						originAddress: user.metadata?.originAddress,
						livingAddress: user.metadata?.livingAddress,
						moodFrequency: user.metadata?.moodFrequency,
						birthDate: user.metadata.birthDate?.getTime() || null,
						registrationTimestamp: user.metadata?.registrationTimestamp,
						moodDescriptionShown: user.metadata?.moodDescriptionShown,
					},
					intercomHash: user.intercomHash,
					updateTimestamp: user.updateTimestamp,
					creationTimestamp: user.creationTimestamp,
					companySectors: user.companySectors,
					organizationId: user.organizationId,
					observersId: user.observersId || [],
					partnersId: user.partnersId || [],
					emailAddress: user.emailAddress,
					phoneNumber: user.phoneNumber,
					pictureUrl: user.pictureUrl,
					firstName: user.firstName,
					lastName: user.lastName,
					timeZone: user.timeZone,
					coachId: user.coachId,
					status: user.status,
					hasMfa: user.hasMfa,
					role: user.role,
					id: user.id,
			  } as UserDocument)
			: null;

		if (!user.intercomHash) {
			unset(document, 'intercomHash');
		}

		if (!user.hasMetadata) {
			unset(document, 'metadata');
		}

		if ((user.availabilities || []).length === 0) {
			unset(document, 'availabilities');
		}

		if ((user.certificates || []).length === 0) {
			unset(document, 'certificates');
		}

		if ((user.diplomas || []).length === 0) {
			unset(document, 'diplomas');
		}

		if ((user.modulePushes || []).length === 0) {
			unset(document, 'modulePushes');
		}

		if ((user.partnersId || []).length === 0) {
			unset(document, 'partnersId');
		}

		if ((user.partners || []).length === 0) {
			unset(document, 'partners');
		}

		return document;
	}

	public asEntity(
		document: UserDocument,
		organizations$: (ids: string[]) => Observable<Organization[]>
	): User {
		return document
			? new User(
					document.id,
					UserRole[document.role],
					document.hasMfa,
					document.coachId,
					document.timeZone,
					document.metadata?.isGhost,
					document.lastName,
					document.firstName,
					UserStatus[document.status],
					document.pictureUrl,
					document.emailAddress,
					document.phoneNumber,
					document.partnersId || [],
					document.intercomHash,
					document.observersId || [],
					document.organizationId,
					document.companySectors,
					new UserMetadata(
						UserMoodFrequency[document.metadata?.moodFrequency],
						document.metadata?.moodDescriptionShown,
						document.metadata?.registrationTimestamp,
						document.metadata?.livingAddress,
						document.metadata?.originAddress,
						document.metadata?.linkedInUrl,
						document.metadata?.language,
						document.metadata?.birthDate
							? new Date(document.metadata.birthDate)
							: null,
						document.metadata?.cvUrl,
						document.metadata?.quote
					),
					document.creationTimestamp,
					document.updateTimestamp,
					document.partners,
					(document.diplomas || []).map((diploma) =>
						this._asUserReferenceEntity(diploma)
					),
					(document.certificates || []).map((certificate) =>
						this._asUserReferenceEntity(certificate)
					),
					(document.availabilities || []).map(
						(availability) =>
							new UserAvailability(
								availability.dayIndex,
								availability.endOffset,
								availability.startOffset,
								UserAvailabilityType[availability.type],
								availability.id
							)
					),
					(document.modulePushes || []).map(
						(modulePush) =>
							new UserModulePush(
								modulePush.emailId,
								modulePush.contactId,
								modulePush.contactName,
								modulePush.contactLanguage,
								modulePush.pushOrgModuleId,
								modulePush.contactEmailAddress,
								UserModulePushStatus[modulePush.status]
							)
					),
					document.coachId
						? this._userRepositoryPort.observeUserById(document.coachId)
						: of(null),
					document.organizationId
						? organizations$([document.organizationId]).pipe(map(first))
						: of(null),
					!isEmpty(document.partnersId)
						? organizations$(document.partnersId)
						: of([])
			  )
			: null;
	}

	private _asUserModulePushDocument(
		modulePush: UserModulePush
	): UserModulePushDocument {
		return modulePush
			? {
					contactEmailAddress: modulePush.contactEmailAddress,
					pushOrgModuleId: modulePush.pushOrgModuleId,
					contactLanguage: modulePush.contactLanguage,
					contactName: modulePush.contactName,
					contactId: modulePush.contactId,
					emailId: modulePush.emailId,
					status: modulePush.status,
			  }
			: null;
	}

	private _asUserAvailabilityDocument(
		availability: UserAvailability
	): UserAvailabilityDocument {
		return availability
			? {
					startOffset: availability.startOffset,
					endOffset: availability.endOffset,
					dayIndex: availability.dayIndex,
					type: availability.type,
					id: availability.id,
			  }
			: null;
	}

	private _asUserReferenceDocument(
		entity: UserReference
	): UserReferenceDocument {
		return entity
			? {
					id: entity.id,
					startDate: entity.startDate?.getTime(),
					endDate: entity.endDate?.getTime(),
					institution: entity.institution,
					name: entity.name,
			  }
			: null;
	}

	private _asUserReferenceEntity(
		document: UserReferenceDocument
	): UserReference {
		return new UserReference(
			document.id,
			document.name,
			document.endDate ? new Date(document.endDate) : null,
			document.startDate ? new Date(document.startDate) : null,
			document.institution
		);
	}
}
